import { Component, OnInit, OnDestroy, HostListener, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { RouteInfo, ROUTES, getItemId } from '../../classes/routes';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { CommandPaletteService } from '../../services/command-palette.service';
import { RecentMenuService } from '../../services/recent-menu.service';
import { PinnedMenuService } from '../../services/pinned-menu.service';
import { ReportsService } from '../../services/reports.service';
import { FULL_NAV_LEVEL } from '../../constants/access-levels';
import { menuPathExplicitOrImplied, portalScopeBlocksPath, isPathGatedBypassReport } from '../../utils/menu-path-access';
import { configFlagEnabled } from '../../route-builder-v2/utils/feature-flag';
import { buildAnchorReportsMap } from '../sidebar/sidebar-menu.util';
import { Subject, combineLatest, of } from 'rxjs';
import { takeUntil, startWith, catchError, debounceTime } from 'rxjs/operators';

export interface MenuSearchItem {
	id: string;
	path: string;
	title: string;
	section: string;
	icon: string;
	isMaterialIcon: boolean;
	/** The 2-letter menu monogram ('PI', 'RR'), lowercased. '' for material-icon entries. */
	code: string;
	searchIndex: string;
}

const MATERIAL_ICONS = new Set([
	'dashboard', 'groups', 'point_of_sale', 'shopping_cart', 'trending_up',
	'inventory', 'inventory_2', 'local_shipping', 'payments', 'settings',
	'store', 'warehouse', 'article', 'label', 'star', 'search',
	'person', 'people', 'business', 'receipt', 'account_balance',
	'assessment', 'bar_chart', 'pie_chart', 'show_chart', 'timeline',
	'description', 'folder', 'note', 'list', 'view_list', 'grid_view',
	'home', 'menu', 'apps', 'build', 'engineering', 'science',
	'school', 'local_offer', 'sell', 'storefront', 'category',
	'all_inclusive', 'note_add', 'launch', 'add', 'remove', 'close',
	'check', 'edit', 'delete', 'save', 'print', 'share', 'download',
	'upload', 'attach_file', 'link', 'mail', 'phone', 'chat',
	'message', 'notifications', 'help', 'info', 'warning', 'error',
	'visibility', 'lock', 'security', 'admin_panel_settings',
	'manage_accounts', 'supervisor_account', 'badge', 'credit_card',
	'account_balance_wallet', 'monetization_on', 'attach_money',
	'request_quote', 'calculate', 'analytics', 'insights',
	'local_atm', 'money', 'paid', 'price_check', 'savings',
	'stacked_bar_chart', 'table_chart', 'leaderboard',
]);

const TOP_LEVEL_MENU_PATHS = new Set(
	ROUTES
		.map(route => route?.path || '')
		.filter(path => typeof path === 'string' && path.startsWith('/'))
);

function isMaterialIconName(icon: string): boolean {
	if (!icon) {return false;}
	if (MATERIAL_ICONS.has(icon)) {return true;}
	if (icon.includes('_') && icon === icon.toLowerCase()) {return true;}
	return false;
}

@Component({
	selector: 'app-menu-search',
	templateUrl: './menu-search.component.html',
	styleUrls: ['./menu-search.component.scss']
})
export class MenuSearchComponent implements OnInit, OnDestroy {
	private destroy$ = new Subject<void>();
	private recentSub: { unsubscribe: () => void } | null = null;
	private pinnedSub: { unsubscribe: () => void } | null = null;
	private alwaysAllowed = ['/logout', '/auth/permission'];
	private fullNavLevel = FULL_NAV_LEVEL;

	isOpen = false;
	searchQuery = '';
	selectedIndex = 0;
	allowedItems: MenuSearchItem[] = [];
	user: any;
	config: any;
	reports: any[] = [];
	recentIds: string[] = [];
	pinnedIds: string[] = [];

	constructor(
		private router: Router,
		private commandPalette: CommandPaletteService,
		private recentMenu: RecentMenuService,
		private pinnedMenu: PinnedMenuService,
		private globalSearch: GlobalSearchService,
		private reportsService: ReportsService,
		private cdr: ChangeDetectorRef
	) {}

	ngOnInit(): void {
		combineLatest([
			this.globalSearch.user.pipe(startWith(null)),
			this.globalSearch.configsubscription.pipe(startWith(null)),
			this.reportsService.getReports().pipe(
				startWith([]),
				catchError(() => of([]))
			)
		]).pipe(
			debounceTime(0),
			takeUntil(this.destroy$)
		).subscribe(([user, config, reports]) => {
			this.user = user;
			this.config = config;
			this.reports = Array.isArray(reports) ? reports : [];
			this.allowedItems = this.buildAllowedItems();
			this.cdr.markForCheck();
		});

		this.commandPalette.openRequested$.pipe(takeUntil(this.destroy$)).subscribe(() => this.open());

		this.recentIds = this.recentMenu.recentItems;
		this.pinnedIds = this.pinnedMenu.pinnedItems;
		this.recentSub = this.recentMenu.subscribe(ids => {
			this.recentIds = ids;
			this.cdr.markForCheck();
		});
		this.pinnedSub = this.pinnedMenu.subscribe(ids => {
			this.pinnedIds = ids;
			this.cdr.markForCheck();
		});
	}

	private pathAllowed(path: string): boolean {
		const normalizedPath = this.normalizePath(path);
		if (portalScopeBlocksPath(normalizedPath, this.user)) {
			return false;
		}
		const { fullaccess, access_permissions } = this.user?.user || {};
		const level = fullaccess != null ? parseInt(String(fullaccess), 10) : NaN;
		if (!Number.isNaN(level) && level >= this.fullNavLevel) {return true;}
		if (!Array.isArray(access_permissions) || access_permissions.length === 0) {return false;}

		const permissions = new Set(
			access_permissions
				.map((p: any) => this.normalizePath(String(p || '')))
				.filter((p: string) => p.length > 0)
		);

		if ((normalizedPath === '/storefront' || normalizedPath.startsWith('/storefront/')) &&
			permissions.has('/system/storefront-settings')) {
			return true;
		}

		if (!permissions.has(normalizedPath)) {
			return menuPathExplicitOrImplied(normalizedPath, Array.from(permissions));
		}

		// Enforce module-level visibility: if user cannot access a top-level module path,
		// module children should not populate command palette results.
		const modulePath = this.getModulePath(normalizedPath);
		if (modulePath && modulePath !== normalizedPath && TOP_LEVEL_MENU_PATHS.has(modulePath)) {
			return permissions.has(modulePath);
		}

		return true;
	}

	private normalizePath(path: string): string {
		if (!path) {return '';}
		const trimmed = path.trim();
		const noQuery = trimmed.split('?')[0].split('#')[0];
		const withLeadingSlash = noQuery.startsWith('/') ? noQuery : `/${noQuery}`;
		return withLeadingSlash.replace(/\/+$/, '') || '/';
	}

	private getModulePath(path: string): string {
		const normalized = this.normalizePath(path);
		const segments = normalized.split('/').filter(Boolean);
		return segments.length > 0 ? `/${segments[0]}` : normalized;
	}

	private hasReportTitle(title: string): boolean {
		return (this.reports || []).some((r: any) => r && typeof r === 'object' && r.title === title);
	}

	private hasSalesRouteBuilderGrant(path: string): boolean {
		return this.hasReportTitle('Sales Route Builder') || this.pathAllowed(path);
	}

	private showMenuItem(menuItem: RouteInfo): boolean {
		if (menuItem.storefrontStandaloneOnly) {
			return this.config?.env?.storefrontStandalone === true &&
				(!menuItem.package || menuItem.package === 'all' || menuItem.package === this.config?.env?.package);
		}
		if (!menuItem.package) {return true;}
		return menuItem.package === 'all' || menuItem.package === this.config?.env?.package;
	}

	private showChild(item: any): boolean {
		if (item.requireConfigTruthy && !configFlagEnabled(this.config, item.requireConfigTruthy)) {return false;}
		if (item.hideWhenStorefrontStandalone && this.config?.env?.storefrontStandalone) {return false;}
		if (!item.package) {return true;}
		return item.package === 'all' || item.package === this.config?.env?.package;
	}

	private anchorReportsMap(): Map<string, any[]> {
		return buildAnchorReportsMap(this.reports, this.config?.env?.package);
	}

	private getDisplayTitle(item: any): string {
		return item?.title || '';
	}

	private buildSearchIndex(item: any, section: string): string {
		const fields = [
			item?.title,
			item?.longtitle,
			item?.label,
			section,
		];
		return fields
			.filter((v: any) => typeof v === 'string' && v.trim().length > 0)
			.map((v: string) => v.toLowerCase())
			.join(' ');
	}

	private makeItem(
		id: string,
		path: string,
		title: string,
		section: string,
		icon: string,
		parentIcon?: string,
		searchIndex?: string
	): MenuSearchItem {
		const resolvedIcon = icon || parentIcon || '';
		const isMaterial = isMaterialIconName(resolvedIcon);
		// Only a monogram is a code. Material icon names ('trending_up') are
		// decorative and would match half the menu on a two-letter query.
		const code = isMaterial ? '' : (resolvedIcon || '').toLowerCase();
		const base = searchIndex || `${title} ${section}`.toLowerCase();
		return {
			id, path, title, section,
			icon: resolvedIcon,
			isMaterialIcon: isMaterial,
			code,
			// The monogram and the url are both things people type. They go in the
			// index so they match at all, and into scoreItem() so they rank.
			searchIndex: `${base} ${code} ${path}`.toLowerCase()
		};
	}

	private buildAllowedItems(): MenuSearchItem[] {
		if (!this.user?.user) {return [];}
		const out: MenuSearchItem[] = [];
		const anchorMap = this.anchorReportsMap();

		const menuItems = ROUTES.filter(m => this.alwaysAllowed.includes(m.path) || this.pathAllowed(m.path));

		for (const menuItem of menuItems) {
			if (!this.showMenuItem(menuItem)) {continue;}
			const parentIcon = menuItem.icon || '';

			if (!menuItem.children || menuItem.children.length === 0) {
				if (this.alwaysAllowed.includes(menuItem.path) || this.pathAllowed(menuItem.path)) {
					out.push(this.makeItem(
						getItemId(menuItem.path), menuItem.path,
						this.getDisplayTitle(menuItem), menuItem.title, parentIcon, undefined,
						this.buildSearchIndex(menuItem, menuItem.title)
					));
				}
				continue;
			}

			for (const child of menuItem.children) {
				if (!this.showChild(child)) {continue;}
				if (!child.children || child.children.length === 0) {
					if (this.pathAllowed(child.path)) {
						out.push(this.makeItem(
							getItemId(child.path), child.path,
							this.getDisplayTitle(child), menuItem.title,
							(child.icon as string) || '', parentIcon,
							this.buildSearchIndex(child, menuItem.title)
						));
					}
				} else {
					for (const gc of child.children) {
						if (this.pathAllowed(gc.path)) {
							out.push(this.makeItem(
								getItemId(gc.path), gc.path,
								this.getDisplayTitle(gc), menuItem.title,
								(gc as any).icon || '', parentIcon,
								this.buildSearchIndex(gc, menuItem.title)
							));
						}
					}
				}
			}

			if (menuItem.reports?.length) {
				for (const report of menuItem.reports) {
					if (!this.showChild(report) || !report.path) {
						continue;
					}
					let visible = false;
					if (isPathGatedBypassReport(report.path)) {
						visible = this.hasSalesRouteBuilderGrant(report.path);
					} else if (report.bypassReportCatalog) {
						visible = this.pathAllowed(report.path);
					} else {
						visible = this.hasReportTitle(report.title);
					}
					if (!visible) {
						continue;
					}
					out.push(this.makeItem(
						getItemId(report.path), report.path,
						this.getDisplayTitle(report), menuItem.title,
						(report.icon as string) || '', parentIcon,
						this.buildSearchIndex(report, menuItem.title)
					));
				}
			}

			if (menuItem.anchor && anchorMap.has(menuItem.anchor)) {
				for (const tr of anchorMap.get(menuItem.anchor)!) {
					const path = (tr as any).nglink;
					if (path && this.pathAllowed(path)) {
						out.push(this.makeItem(
							getItemId(path), path,
							(tr as any).title || '', menuItem.title,
							'article', parentIcon
						));
					}
				}
			}
		}
		return out;
	}

	get recentItems(): MenuSearchItem[] {
		const idSet = new Set(this.allowedItems.map(i => i.id));
		return this.recentIds
			.filter(id => idSet.has(id))
			.map(id => this.allowedItems.find(i => i.id === id)!)
			.filter(Boolean)
			.slice(0, 5);
	}

	get pinnedItems(): MenuSearchItem[] {
		const idSet = new Set(this.allowedItems.map(i => i.id));
		return this.pinnedIds
			.filter(id => idSet.has(id))
			.map(id => this.allowedItems.find(i => i.id === id)!)
			.filter(Boolean);
	}

	/**
	 * Rank rather than filter. A plain substring match buried the thing you typed
	 * the code for: 'pi' is a substring of "Shipping", "Picking" and "Pick Lists",
	 * so the item whose monogram is literally PI came somewhere down the list, if
	 * at all. Lower score wins; menu order breaks ties so results stay stable.
	 */
	private scoreItem(item: MenuSearchItem, q: string): number {
		const title = item.title.toLowerCase();
		if (item.code) {
			if (item.code === q) {return 0;}
			if (item.code.startsWith(q)) {return 1;}
		}
		if (title.startsWith(q)) {return 2;}
		if (title.split(/[\s/&-]+/).some(w => w.startsWith(q))) {return 3;}
		if (title.includes(q)) {return 4;}
		if (item.searchIndex.includes(q)) {return 5;}
		return -1;
	}

	get filteredItems(): MenuSearchItem[] {
		const q = this.searchQuery.trim().toLowerCase();
		if (!q) {return [];}

		const scored: { item: MenuSearchItem; score: number; order: number }[] = [];
		this.allowedItems.forEach((item, order) => {
			const score = this.scoreItem(item, q);
			if (score >= 0) {scored.push({ item, score, order });}
		});

		scored.sort((a, b) => a.score - b.score || a.order - b.order);
		return scored.slice(0, 20).map(s => s.item);
	}

	get displayItems(): MenuSearchItem[] {
		if (this.searchQuery.trim()) {return this.filteredItems;}
		return [...this.recentItems, ...this.pinnedItems];
	}

	@HostListener('window:keydown', ['$event'])
	handleKeyboard(event: KeyboardEvent): void {
		if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
			event.preventDefault();
			this.toggle();
			return;
		}
		if (!this.isOpen) {return;}
		if (event.key === 'Escape') {
			event.preventDefault();
			this.close();
			return;
		}
		const items = this.displayItems;
		if (event.key === 'ArrowDown') {
			event.preventDefault();
			this.selectedIndex = (this.selectedIndex + 1) % Math.max(items.length, 1);
			this.cdr.markForCheck();
		} else if (event.key === 'ArrowUp') {
			event.preventDefault();
			this.selectedIndex = (this.selectedIndex - 1 + items.length) % Math.max(items.length, 1);
			this.cdr.markForCheck();
		} else if (event.key === 'Enter') {
			event.preventDefault();
			const selected = items[this.selectedIndex];
			if (selected) {this.navigateToItem(selected);}
		}
	}

	toggle(): void {
		this.isOpen = !this.isOpen;
		if (this.isOpen) {
			this.searchQuery = '';
			this.selectedIndex = 0;
			setTimeout(() => this.focusInput(), 50);
		}
		this.cdr.markForCheck();
	}

	open(): void {
		this.isOpen = true;
		this.searchQuery = '';
		this.selectedIndex = 0;
		setTimeout(() => this.focusInput(), 50);
		this.cdr.markForCheck();
	}

	close(): void {
		this.isOpen = false;
		this.searchQuery = '';
		this.cdr.markForCheck();
	}

	private focusInput(): void {
		const el = document.querySelector('.menu-search-input') as HTMLInputElement;
		el?.focus();
	}

	onSearchInput(event: Event): void {
		const input = event.target as HTMLInputElement;
		this.searchQuery = input.value;
		this.selectedIndex = 0;
		this.cdr.markForCheck();
	}

	navigateToItem(item: MenuSearchItem): void {
		if (!this.pathAllowed(item.path)) {return;}
		this.router.navigate([item.path]);
		this.recentMenu.addRecent(item.id);
		this.close();
	}

	togglePin(item: MenuSearchItem, event: Event): void {
		event.stopPropagation();
		this.pinnedMenu.togglePin(item.id);
		this.pinnedIds = this.pinnedMenu.pinnedItems;
		this.cdr.markForCheck();
	}

	isPinned(item: MenuSearchItem): boolean {
		return this.pinnedIds.includes(item.id);
	}

	trackById(_index: number, item: MenuSearchItem): string {
		return item.id;
	}

	ngOnDestroy(): void {
		this.recentSub?.unsubscribe();
		this.pinnedSub?.unsubscribe();
		this.destroy$.next();
		this.destroy$.complete();
	}
}
